import { supabase } from '../utils/supabaseClient';
import { getDisplayName } from '../utils/formatters';
import { companyDateKey } from '../utils/dateFormatters';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';

/**
 * Enhanced Calendar Service with Work Order Integration
 * Provides comprehensive calendar functionality with proper work order linkage
 */
class CalendarService {
  
  /**
   * Get calendar events with server-side filtering (optimized)
   * Uses the get_calendar_events_filtered RPC for better performance
   * Falls back to legacy getCalendarEvents if RPC fails or returns incomplete data
   * @param {string} companyId - Company ID
   * @param {Object} filters - Filter options
   * @param {Date|string} filters.startDate - Start date for filtering
   * @param {Date|string} filters.endDate - End date for filtering
   * @param {string} filters.employeeId - Optional employee filter
   * @param {string} filters.status - Optional status filter
   * @param {number} filters.limit - Maximum number of events (default: 1000)
   * @param {number} filters.offset - Offset for pagination (default: 0)
   * @returns {Promise<{events: Array, totalCount: number}>} Events and total count
   */
  async getCalendarEventsFiltered(companyId, filters = {}) {
    try {
      const {
        startDate = null,
        endDate = null,
        employeeId = null,
        status = null,
        limit = 1000,
        offset = 0
      } = filters;

      // Convert dates to ISO strings if they're Date objects
      const startDateStr = startDate instanceof Date && !isNaN(startDate.getTime())
        ? startDate.toISOString()
        : startDate;
      const endDateStr = endDate instanceof Date && !isNaN(endDate.getTime())
        ? endDate.toISOString()
        : endDate;

      const { data, error } = await supabase.rpc('get_calendar_events_filtered', {
        _company_id: companyId,
        _start_date: startDateStr,
        _end_date: endDateStr,
        _employee_id: employeeId,
        _status_filter: status,
        _limit: limit,
        _offset: offset
      });

      if (error) {
        console.warn('RPC failed, falling back to legacy method:', error.message);
        return this.getCalendarEventsFallback(companyId, filters);
      }

      // Check if we got valid events with customer data
      const events = data?.map(row => row.event) || [];
      const totalCount = data?.length > 0 ? data[0].total_count || events.length : 0;

      // If events have incomplete data (missing customerName), fallback to legacy method
      // This ensures we get complete customer information
      if (events.length > 0) {
        const firstEvent = events[0];
        const hasCustomerData = firstEvent?.extendedProps?.customerName !== undefined;
        
        if (!hasCustomerData) {
          console.warn('RPC returned incomplete data, falling back to legacy method for full customer info');
          return this.getCalendarEventsFallback(companyId, filters);
        }
      }

      // Enrich events with additional client-side data if needed
      const enrichedEvents = this.enrichRpcEvents(events);

      return { events: enrichedEvents, totalCount };
    } catch (error) {
      console.error('Error in getCalendarEventsFiltered:', error);
      // Fallback to legacy method on any error
      return this.getCalendarEventsFallback(companyId, filters);
    }
  }

  /**
   * Events in one time window from the same RPC, for the Calendar's live updates. Unlike
   * getCalendarEventsFiltered it THROWS on failure instead of falling back (the fallback ignores
   * the window and answers [] on error, and merging [] would blank the visible range).
   */
  async getCalendarEventsWindow(companyId, { startDate, endDate, employeeId = null }) {
    const { data, error } = await supabase.rpc('get_calendar_events_filtered', {
      _company_id: companyId,
      _start_date: startDate,
      _end_date: endDate,
      _employee_id: employeeId,
      _status_filter: null,
      _limit: 3000,
      _offset: 0
    });
    if (error) throw error;
    return this.enrichRpcEvents((data || []).map(row => row.event));
  }

  /**
   * Enrich RPC events with additional client-side data
   * This supplements the data from the RPC with what we can get client-side
   * @param {Array} events - Raw events from RPC
   * @returns {Array} Enriched events
   */
  enrichRpcEvents(events) {
    if (!events || events.length === 0) return [];

    return events.map(event => {
      const props = event.extendedProps || {};
      
      // If we already have customerName, just return as-is
      if (props.customerName && props.customerName !== 'Unknown Customer') {
        return event;
      }

      // Event already has what we need
      return event;
    });
  }

  /**
   * Fallback method using legacy getCalendarEvents for complete data
   * @param {string} companyId - Company ID
   * @param {Object} filters - Filter options
   * @returns {Promise<{events: Array, totalCount: number}>} Events and total count
   */
  async getCalendarEventsFallback(companyId, filters) {
    try {
      const { startDate, endDate, employeeId } = filters;
      
      // Use the legacy method which has full enrichment
      const legacyEvents = await this.getCalendarEvents(
        companyId,
        startDate instanceof Date ? startDate : (startDate ? new Date(startDate) : null),
        endDate instanceof Date ? endDate : (endDate ? new Date(endDate) : null),
        employeeId,
        false // applyDateFilter - false for calendar display (90-day window)
      );

      // Apply client-side filtering for status if needed
      let filteredEvents = legacyEvents;
      if (filters.status && filters.status !== 'all') {
        filteredEvents = legacyEvents.filter(event => {
          const eventStatus = event.extendedProps?.workOrderStatus || event.extendedProps?.status;
          const statusMap = {
            [WORK_ORDER_STATUS.SCHEDULED]: [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.APPROVED],
            [WORK_ORDER_STATUS.IN_PROGRESS]: [WORK_ORDER_STATUS.IN_PROGRESS, 'in progress'],
            [WORK_ORDER_STATUS.COMPLETED]: [WORK_ORDER_STATUS.COMPLETED],
            [WORK_ORDER_STATUS.ON_HOLD]: [WORK_ORDER_STATUS.ON_HOLD, 'on hold', 'hold'],
            [WORK_ORDER_STATUS.CANCELLED]: [WORK_ORDER_STATUS.CANCELLED]
          };
          const allowedStatuses = statusMap[filters.status] || [];
          return allowedStatuses.some(s => eventStatus?.toLowerCase().includes(s));
        });
      }

      return { events: filteredEvents, totalCount: filteredEvents.length };
    } catch (error) {
      console.error('Error in fallback method:', error);
      return { events: [], totalCount: 0 };
    }
  }

  /**
   * Get dashboard KPI counts from server (pre-computed)
   * Uses the get_calendar_dashboard_counts RPC for instant KPI loads
   * @param {string} companyId - Company ID
   * @param {Date|string} targetDate - Target date for KPI calculations (default: today)
   * @returns {Promise<Object>} Dashboard counts object
   */
  async getDashboardCounts(companyId, targetDate = new Date()) {
    try {
      // Convert to YYYY-MM-DD in the COMPANY's timezone.
      //
      // This used to be `targetDate.toISOString().split('T')[0]`, i.e. the UTC calendar day —
      // the other half of a double-UTC bug. The server then bucketed with a bare `::date`, which
      // also casts in the UTC session zone. So after 17:00 Pacific the client asked for TOMORROW
      // and the server answered in UTC, and every evening job dropped out of due_today / at_risk
      // / late_starts / completed_today for the rest of the night. Server half fixed in
      // migration 20260813g; `_target_date` now means "a calendar day in the company's timezone".
      //
      // companyDateKey() formats via en-CA (YYYY-MM-DD) in the company zone. It only works
      // because UserContext now resolves the timezone at bootstrap — before that it silently
      // fell back to browser-local.
      const targetDateStr = targetDate instanceof Date && !isNaN(targetDate.getTime())
        ? companyDateKey(targetDate)
        : targetDate;

      const { data, error } = await supabase.rpc('get_calendar_dashboard_counts', {
        _company_id: companyId,
        _target_date: targetDateStr
      });

      if (error) {
        console.error('Error fetching dashboard counts:', error);
        return null;
      }

      // RPC returns JSONB object with counts
      return data || null;
    } catch (error) {
      console.error('Error in getDashboardCounts:', error);
      return null;
    }
  }

  /**
   * Get calendar events with full work order context (legacy method - kept for backward compatibility)
   * @param {string} companyId - Company ID
   * @param {Date} startDate - Start date for filtering
   * @param {Date} endDate - End date for filtering
   * @param {string} employeeId - Optional employee filter
   * @param {boolean} applyDateFilter - Whether to apply date filtering (default: true for dashboard, false for calendar display)
   * @returns {Promise<Array>} Calendar events with work order context
   */
  async getCalendarEvents(companyId, startDate = null, endDate = null, employeeId = null, applyDateFilter = true) {
    try {
      // Build query with supabase client
      let query = supabase
        .from('schedule_events')
        .select('*')
        .eq('company_id', companyId);

      // Date filtering: exact range for dashboard, 90-day window for calendar
      if (startDate instanceof Date && !isNaN(startDate.getTime())) {
        if (applyDateFilter) {
          query = query.gte('start_time', startDate.toISOString());
        } else {
          const expandedStart = new Date(startDate);
          expandedStart.setDate(expandedStart.getDate() - 90);
          query = query.gte('start_time', expandedStart.toISOString());
        }
      }
      if (endDate instanceof Date && !isNaN(endDate.getTime())) {
        if (applyDateFilter) {
          query = query.lte('end_time', endDate.toISOString());
        } else {
          const expandedEnd = new Date(endDate);
          expandedEnd.setDate(expandedEnd.getDate() + 90);
          query = query.lte('end_time', expandedEnd.toISOString());
        }
      }

      // Add employee filtering
      if (employeeId) {
        query = query.eq('employee_id', employeeId);
      }

      query = query.order('start_time', { ascending: true });

      const { data: events, error } = await query;

      // Combine schedule_events AND work orders that don't have schedule_events
      // This ensures multi-day jobs (with schedule_events) AND single-day jobs (WO only) both appear
      let allCalendarEvents = [];

      // 1. Add enriched schedule_events
      const scheduleEventWOIds = new Set();
      if (!error && events && events.length > 0) {
        const enrichedEvents = await this.enrichEvents(events, companyId);
        allCalendarEvents = [...enrichedEvents];
        // Track which work orders already have schedule_events
        events.forEach(e => { if (e.work_order_id) scheduleEventWOIds.add(e.work_order_id); });
      }

      // 2. Add work orders that DON'T have schedule_events (single-day jobs)
      const workOrderEvents = await this.getSimpleWorkOrderEvents(companyId, startDate, endDate, employeeId, applyDateFilter);
      const woOnlyEvents = workOrderEvents.filter(woEvent => {
        // Extract the real WO id (getSimpleWorkOrderEvents prefixes with 'wo_')
        const woId = woEvent.id?.startsWith('wo_') ? woEvent.id.slice(3) : woEvent.extendedProps?.workOrderId;
        return !scheduleEventWOIds.has(woId);
      });
      allCalendarEvents = [...allCalendarEvents, ...woOnlyEvents];

      return allCalendarEvents;
    } catch (error) {
      return [];
    }
  }

  /**
   * Enrich schedule_events with related data
   * @param {Array} events - Raw schedule_events
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>} Enriched events
   */
  async enrichEvents(events, companyId) {
    try {
      // BATCH approach: collect all unique IDs, then 3 parallel queries instead of 3×N serial queries
      const woIds = [...new Set(events.map(e => e.work_order_id).filter(Boolean))];
      const userIds = [...new Set(events.map(e => e.user_id || e.employee_id).filter(Boolean))];

      // Batch fetch all related data in parallel
      const [woResult, userResult] = await Promise.all([
        woIds.length > 0
          ? supabase.from('work_orders').select('id,title,work_order_number,status,customer_id,estimated_duration,total_amount,service_address_line_1,service_city,service_state,description,priority,notes,internal_notes').in('id', woIds)
          : { data: [] },
        userIds.length > 0
          ? supabase.from('users').select('id,first_name,last_name,email').in('id', userIds)
          : { data: [] },
      ]);

      // Build lookup Maps
      const woMap = new Map();
      (woResult.data || []).forEach(wo => woMap.set(wo.id, wo));

      const userMap = new Map();
      (userResult.data || []).forEach(u => userMap.set(u.id, u));

      // Now batch-fetch customers from the work order customer_ids
      const customerIds = [...new Set(
        (woResult.data || []).map(wo => wo.customer_id).filter(Boolean)
      )];
      let customerMap = new Map();
      if (customerIds.length > 0) {
        const { data: custData } = await supabase
          .from('customers')
          .select('id,name,company_name,first_name,last_name,phone,email,street_address,city,state,zip_code')
          .in('id', customerIds);
        (custData || []).forEach(c => customerMap.set(c.id, c));
      }

      // Enrich each event from the Maps — zero additional queries
      return events.map(event => {
        const enrichedEvent = { ...event };
        enrichedEvent.work_order = woMap.get(event.work_order_id) || null;
        const custId = event.customer_id || enrichedEvent.work_order?.customer_id;
        enrichedEvent.customer = custId ? customerMap.get(custId) || null : null;
        enrichedEvent.user = userMap.get(event.user_id || event.employee_id) || null;
        return this.formatEnrichedEvent(enrichedEvent);
      });
    } catch (error) {
      console.error('Error enriching events:', error);
      return events.map(event => this.formatBasicEvent(event));
    }
  }

  /**
   * Get simple work order events without complex joins
   * @param {string} companyId - Company ID
   * @param {Date} startDate - Start date for filtering
   * @param {Date} endDate - End date for filtering
   * @param {string} employeeId - Optional employee filter
   * @param {boolean} applyDateFilter - Whether to apply date filtering (default: true for dashboard, false for calendar display)
   * @returns {Promise<Array>} Work order events
   */
  async getSimpleWorkOrderEvents(companyId, startDate = null, endDate = null, employeeId = null, applyDateFilter = true) {
    try {
      // console.log('Loading work order events...');

      // Include customer and employee data in the query to avoid "Loading..." placeholders
      let query = supabase
        .from('work_orders')
        .select(`
          *,
          customers!work_orders_customer_id_fkey(id, name, company_name, first_name, last_name, phone, email, street_address, city, state, zip_code),
          users!work_orders_assigned_to_fkey(id, first_name, last_name, email)
        `)
        .eq('company_id', companyId)
        .not('scheduled_start', 'is', null)
        .not('scheduled_end', 'is', null);

      // Date filtering: exact range for dashboard, 90-day window for calendar
      if (startDate instanceof Date && !isNaN(startDate.getTime())) {
        if (applyDateFilter) {
          query = query.gte('scheduled_start', startDate.toISOString());
        } else {
          const expandedStart = new Date(startDate);
          expandedStart.setDate(expandedStart.getDate() - 90);
          query = query.gte('scheduled_start', expandedStart.toISOString());
        }
      }
      if (endDate instanceof Date && !isNaN(endDate.getTime())) {
        if (applyDateFilter) {
          query = query.lte('scheduled_end', endDate.toISOString());
        } else {
          const expandedEnd = new Date(endDate);
          expandedEnd.setDate(expandedEnd.getDate() + 90);
          query = query.lte('scheduled_end', expandedEnd.toISOString());
        }
      }

      // Add employee filtering (unified: assigned_to)
      if (employeeId) {
        query = query.eq('assigned_to', employeeId);
      }

      query = query.order('scheduled_start', { ascending: true });

      const { data: workOrders, error } = await query;

      // console.log('Work orders:', workOrders?.length);

      if (!error && workOrders) {
        const formattedEvents = workOrders.map(wo => this.formatWorkOrderAsEvent(wo));
        return formattedEvents;
      } else {
        console.error('Failed to load work orders:', error);
        return [];
      }
    } catch (error) {
      console.error('Error loading work order events:', error);
      return [];
    }
  }

  /**
   * Get work orders that have scheduling but no schedule_events
   * @param {string} companyId - Company ID
   * @param {Date} startDate - Start date for filtering
   * @param {Date} endDate - End date for filtering
   * @param {string} employeeId - Optional employee filter
   * @returns {Promise<Array>} Work order events
   */
  async getWorkOrderEvents(companyId, startDate = null, endDate = null, employeeId = null) {
    try {
      // console.log('Loading work order events directly...');

      // ✅ FIX: Use scheduled_start/scheduled_end (actual column names in work_orders table)
      // Note: customers() embedded select removed - no FK exists, fetch separately
      let query = supabase
        .from('work_orders')
        .select('id,title,work_order_number,quote_number,status,customer_id,assigned_to,assigned_technician_id,scheduled_start,scheduled_end,estimated_duration,total_amount,description,priority,notes,internal_notes,service_address_line_1,service_city,service_state,users!work_orders_assigned_to_fkey(id,first_name,last_name,email)')
        .eq('company_id', companyId)
        .not('scheduled_start', 'is', null)
        .not('scheduled_end', 'is', null)
        .not('status', 'in', `(${WORK_ORDER_STATUS.CANCELLED})`);

      // Add date filtering - only apply if dates are valid
      if (startDate instanceof Date && !isNaN(startDate.getTime())) {
        query = query.gte('scheduled_start', startDate.toISOString());
      }
      if (endDate instanceof Date && !isNaN(endDate.getTime())) {
        query = query.lte('scheduled_end', endDate.toISOString());
      }

      // Add employee filtering (unified: assigned_to)
      if (employeeId) {
        query = query.eq('assigned_to', employeeId);
      }

      query = query.order('scheduled_start', { ascending: true });

      const { data: workOrders, error } = await query;

      if (!error && workOrders && workOrders.length > 0) {
        // Batch fetch customers by unique customer_ids
        const customerIds = [...new Set(workOrders.map(wo => wo.customer_id).filter(Boolean))];
        let customersMap = {};
        if (customerIds.length > 0) {
          const { data: customers } = await supabase
            .from('customers')
            .select('id,name,company_name,first_name,last_name,phone,email')
            .in('id', customerIds);
          if (customers) {
            customers.forEach(c => { customersMap[c.id] = c; });
          }
        }
        // Merge customers into work orders
        const workOrdersWithCustomers = workOrders.map(wo => ({
          ...wo,
          customers: wo.customer_id ? customersMap[wo.customer_id] || null : null
        }));
        return workOrdersWithCustomers.map(wo => this.formatWorkOrderAsEvent(wo));
      } else {
        console.error('Failed to load work order events:', error);
        return [];
      }
    } catch (error) {
      console.error('Error loading work order events:', error);
      return [];
    }
  }

  /**
   * Format work order as calendar event
   * @param {Object} workOrder - Work order data
   * @returns {Object} Formatted event
   */
  formatWorkOrderAsEvent(workOrder) {
    const customer = workOrder.customers;
    const user = workOrder.users;

    const customerName = getDisplayName(customer, 'Unknown Customer');

    const employeeName = user ?
      (getDisplayName(user, null) || user.email || 'Unassigned') :
      'Unassigned';

    const serviceAddress = [workOrder.service_address_line_1, workOrder.service_city, workOrder.service_state]
      .filter(Boolean).join(', ');

    // Clean title format: Customer Name - Job Title (mobile-friendly, truncated)
    const truncate = (str, maxLen = 25) => {
      if (!str) return '';
      return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
    };

    const title = `${truncate(customerName)} - ${truncate(workOrder.title || 'Service Call', 30)}`;

    return {
      id: `wo_${workOrder.id}`, // Prefix to avoid conflicts with schedule_events
      title: title,
      // ✅ FIX: Use scheduled_start/scheduled_end (actual column names)
      start: workOrder.scheduled_start || workOrder.start_time,
      end: workOrder.scheduled_end || workOrder.end_time,
      backgroundColor: this.getEventColor('work_order', workOrder.status),
      borderColor: this.getEventBorderColor('work_order', workOrder.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: workOrder.id,
        workOrderNumber: workOrder.work_order_number,
        workOrderStage: workOrder.status,
        workOrderStatus: workOrder.status,
        customerId: workOrder.customer_id,
        customerName: customerName,
        customerPhone: customer?.phone || null,
        customerEmail: customer?.email || null,
        customerAddress: serviceAddress,
        employeeId: workOrder.assigned_to,
        employeeName: employeeName,
        serviceAddress: serviceAddress,
        estimatedDuration: workOrder.estimated_duration,
        totalAmount: workOrder.total_amount,
        eventType: 'work_order',
        status: workOrder.status || 'scheduled',
        description: workOrder.description,
        priority: workOrder.priority || 'normal',
        notes: workOrder.notes || workOrder.internal_notes || null,
        // Include raw workOrder for reference
        rawWorkOrder: workOrder
      }
    };
  }

  /**
   * Create a new schedule event
   * @param {Object} eventData - Event data
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Created event
   */
  async createScheduleEvent(eventData, companyId) {
    try {
      const { data, error } = await supabase
        .from('schedule_events')
        .insert({
          ...eventData,
          company_id: companyId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create schedule event: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating schedule event:', error);
      throw error;
    }
  }
  
  /**
   * Update an existing schedule event
   * @param {string} eventId - Event ID
   * @param {Object} updates - Updates to apply
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Updated event
   */
  async updateScheduleEvent(eventId, updates, companyId) {
    try {
      const { data, error } = await supabase
        .from('schedule_events')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update schedule event: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error updating schedule event:', error);
      throw error;
    }
  }
  
  /**
   * Delete a schedule event
   * @param {string} eventId - Event ID
   * @param {string} companyId - Company ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteScheduleEvent(eventId, companyId) {
    try {
      const { error } = await supabase
        .from('schedule_events')
        .delete()
        .eq('id', eventId)
        .eq('company_id', companyId);

      return !error;
    } catch (error) {
      console.error('Error deleting schedule event:', error);
      return false;
    }
  }
  
  /**
   * Update schedule event(s) by work order ID
   * @param {string} workOrderId - Work Order ID
   * @param {Object} updates - Fields to update
   * @param {string} companyId - Company ID
   */
  async updateScheduleEventByWorkOrder(workOrderId, updates, companyId) {
    try {
      const { error } = await supabase
        .from('schedule_events')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('work_order_id', workOrderId)
        .eq('company_id', companyId);

      if (error) {
        console.warn('Failed to update schedule event by work order:', error.message);
      }
    } catch (error) {
      console.error('Error updating schedule event by work order:', error);
    }
  }

  /**
   * Delete schedule event(s) by work order ID
   * @param {string} workOrderId - Work Order ID
   * @param {string} companyId - Company ID
   */
  async deleteScheduleEventByWorkOrder(workOrderId, companyId) {
    try {
      const { error } = await supabase
        .from('schedule_events')
        .delete()
        .eq('work_order_id', workOrderId)
        .eq('company_id', companyId);

      if (error) {
        console.warn('Failed to delete schedule event by work order:', error.message);
      }
    } catch (error) {
      console.error('Error deleting schedule event by work order:', error);
    }
  }

  /**
   * Update work order scheduling (this will automatically sync to schedule_events via trigger)
   * @param {string} workOrderId - Work Order ID
   * @param {Object} schedulingData - Scheduling data
   * @param {string} companyId - Company ID
   * @returns {Promise<Object>} Updated work order
   */
  async updateWorkOrderScheduling(workOrderId, schedulingData, companyId) {
    try {
      // Map legacy keys to unified columns
      const body = { ...schedulingData };
      if (body.start_time) { body.scheduled_start = body.start_time; delete body.start_time; }
      if (body.end_time) { body.scheduled_end = body.end_time; delete body.end_time; }
      if (body.assigned_technician_id) { body.assigned_to = body.assigned_technician_id; delete body.assigned_technician_id; }

      const { data, error } = await supabase
        .from('work_orders')
        .update(body)
        .eq('id', workOrderId)
        .eq('company_id', companyId)
        .select();

      if (error) {
        throw new Error(`Failed to update work order scheduling: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('Failed to update work order scheduling: no rows updated (possible RLS or company mismatch).');
      }

      return data[0];
    } catch (error) {
      console.error('Error updating work order scheduling:', error);
      throw error;
    }
  }

  /**
   * Format enriched event for calendar
   * @param {Object} event - Enriched event data
   * @returns {Object} Formatted event
   */
  formatEnrichedEvent(event) {
    const workOrder = event.work_order;
    const customer = event.customer;
    const user = event.user;

    const customerName = getDisplayName(customer, 'Unknown Customer');

    const employeeName = user ?
      (getDisplayName(user, null) || user.email || 'Unassigned') :
      'Unassigned';

    const serviceAddress = workOrder ?
      [workOrder.service_address_line_1, workOrder.service_city, workOrder.service_state]
        .filter(Boolean).join(', ') : null;

    return {
      id: event.id,
      title: event.title || 'Untitled Event',
      start: event.start_time,
      end: event.end_time,
      allDay: false, // ✅ FIX: Explicitly set to false to prevent all-day rendering
      backgroundColor: this.getEventColor(event.event_type, event.status),
      borderColor: this.getEventBorderColor(event.event_type, event.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: event.work_order_id,
        workOrderNumber: workOrder?.work_order_number,
        workOrderStage: workOrder?.status,
        workOrderStatus: workOrder?.status,
        customerId: event.customer_id,
        customerName: customerName,
        employeeId: event.user_id || event.employee_id, // CRITICAL FIX: use user_id (populated) not employee_id (NULL)
        employeeName: employeeName,
        serviceAddress: serviceAddress,
        estimatedDuration: workOrder?.estimated_duration,
        totalAmount: workOrder?.total_amount,
        eventType: event.event_type || 'appointment',
        status: event.status || 'scheduled',
        description: event.description,
        priority: workOrder?.priority || 'normal',
        notes: workOrder?.notes || workOrder?.internal_notes || null
      }
    };
  }

  /**
   * Format basic event for calendar (no enrichment)
   * @param {Object} event - Basic event data
   * @returns {Object} Formatted event
   */
  formatBasicEvent(event) {
    return {
      id: event.id,
      title: event.title || 'Untitled Event',
      start: event.start_time,
      end: event.end_time,
      allDay: false, // ✅ FIX: Explicitly set to false to prevent all-day rendering
      backgroundColor: this.getEventColor(event.event_type, event.status),
      borderColor: this.getEventBorderColor(event.event_type, event.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: event.work_order_id,
        customerId: event.customer_id,
        employeeId: event.user_id || event.employee_id, // CRITICAL FIX: use user_id (populated) not employee_id (NULL)
        eventType: event.event_type || 'appointment',
        status: event.status || 'scheduled',
        description: event.description,
        priority: event.priority || 'normal',
        notes: event.notes || event.internal_notes || null
      }
    };
  }

  /**
   * Format work order as basic calendar event
   * @param {Object} workOrder - Work order data
   * @returns {Object} Formatted event
   */
  formatWorkOrderAsBasicEvent(workOrder) {
    const title = workOrder.title || `Work Order: ${workOrder.job_number || workOrder.quote_number || 'Untitled'}`;

    return {
      id: `wo_${workOrder.id}`, // Prefix to avoid conflicts
      title: title,
      // ✅ FIX: Use scheduled_start/scheduled_end (actual column names)
      start: workOrder.scheduled_start || workOrder.start_time,
      end: workOrder.scheduled_end || workOrder.end_time,
      allDay: false, // ✅ FIX: Explicitly set to false to prevent all-day rendering
      backgroundColor: this.getEventColor('work_order', workOrder.status),
      borderColor: this.getEventBorderColor('work_order', workOrder.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: workOrder.id,
        workOrderStage: workOrder.status,
        workOrderStatus: workOrder.status,
        customerId: workOrder.customer_id,
        customerName: 'Loading...', // Will be loaded separately if needed
        employeeId: workOrder.assigned_to,
        employeeName: 'Loading...', // Will be loaded separately if needed
        serviceAddress: [workOrder.service_address_line_1, workOrder.service_city, workOrder.service_state]
          .filter(Boolean).join(', ') || null,
        estimatedDuration: workOrder.estimated_duration,
        totalAmount: workOrder.total_amount,
        eventType: 'work_order',
        status: workOrder.status || 'scheduled',
        description: workOrder.description,
        priority: workOrder.priority || 'normal',
        notes: workOrder.notes || workOrder.internal_notes || null
      }
    };
  }

  /**
   * Get employee availability for scheduling
   * @param {string} employeeId - Employee ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>} Availability slots
   */
  async getEmployeeAvailability(employeeId, startDate, endDate, companyId) {
    try {
      const { data, error } = await supabase
        .from('schedule_events')
        .select('start_time,end_time,title,status')
        .eq('employee_id', employeeId)
        .gte('start_time', startDate.toISOString())
        .lte('end_time', endDate.toISOString());

      if (!error && data) {
        return data;
      } else {
        console.error('Failed to load employee availability:', error);
        return [];
      }
    } catch (error) {
      console.error('Error loading employee availability:', error);
      return [];
    }
  }
  
  /**
   * Check for scheduling conflicts
   * @param {string} employeeId - Employee ID
   * @param {Date} startTime - Proposed start time
   * @param {Date} endTime - Proposed end time
   * @param {string} companyId - Company ID
   * @param {string} excludeEventId - Event ID to exclude from conflict check
   * @returns {Promise<boolean>} True if conflict exists
   */
  async hasSchedulingConflict(employeeId, startTime, endTime, companyId, excludeEventId = null) {
    try {
      let query = supabase
        .from('schedule_events')
        .select('id')
        .eq('employee_id', employeeId)
        .lt('start_time', endTime.toISOString())
        .gt('end_time', startTime.toISOString());

      if (excludeEventId) {
        query = query.neq('id', excludeEventId);
      }

      const { data: conflicts, error } = await query;

      if (!error && conflicts) {
        return conflicts.length > 0;
      } else {
        console.error('Failed to check scheduling conflicts:', error);
        return false;
      }
    } catch (error) {
      console.error('Error checking scheduling conflicts:', error);
      return false;
    }
  }
  
  /**
   * Format calendar event for FullCalendar (from direct query)
   * @param {Object} event - Raw event data from direct query
   * @returns {Object} Formatted event
   */
  formatDirectCalendarEvent(event) {
    const workOrder = event.work_orders;
    const customer = event.customers;
    const user = event.users;

    const customerName = getDisplayName(customer, 'Unknown Customer');

    const employeeName = user ?
      (getDisplayName(user, null) || user.email || 'Unassigned') :
      'Unassigned';

    const serviceAddress = workOrder ?
      [workOrder.service_address_line_1, workOrder.service_city, workOrder.service_state]
        .filter(Boolean).join(', ') : null;

    return {
      id: event.id,
      title: event.title || 'Untitled Event',
      start: event.start_time,
      end: event.end_time,
      backgroundColor: this.getEventColor(event.event_type, event.status),
      borderColor: this.getEventBorderColor(event.event_type, event.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: event.work_order_id,
        workOrderStage: workOrder?.status,
        workOrderStatus: workOrder?.status,
        customerId: event.customer_id,
        customerName: customerName,
        employeeId: event.user_id || event.employee_id, // CRITICAL FIX: use user_id (populated) not employee_id (NULL)
        employeeName: employeeName,
        serviceAddress: serviceAddress,
        estimatedDuration: workOrder?.estimated_duration,
        totalAmount: workOrder?.total_amount,
        eventType: event.event_type || 'appointment',
        status: event.status || 'scheduled',
        description: event.description,
        priority: workOrder?.priority || 'normal',
        notes: workOrder?.notes || workOrder?.internal_notes || null
      }
    };
  }

  /**
   * Format calendar event for FullCalendar (legacy function format)
   * @param {Object} event - Raw event data
   * @returns {Object} Formatted event
   */
  formatCalendarEvent(event) {
    return {
      id: event.id,
      title: event.title,
      start: event.start_time,
      end: event.end_time,
      backgroundColor: this.getEventColor(event.event_type, event.status),
      borderColor: this.getEventBorderColor(event.event_type, event.status),
      textColor: '#ffffff',
      extendedProps: {
        workOrderId: event.work_order_id,
        workOrderStage: event.work_order_stage,
        workOrderStatus: event.work_order_status,
        customerId: event.customer_id,
        customerName: event.customer_name,
        employeeId: event.user_id || event.employee_id, // CRITICAL FIX: use user_id (populated) not employee_id (NULL)
        employeeName: event.employee_name,
        serviceAddress: event.service_address,
        estimatedDuration: event.estimated_duration,
        totalAmount: event.total_amount,
        eventType: event.event_type,
        status: event.status,
        description: event.description,
        priority: event.priority || 'normal',
        notes: event.notes || event.internal_notes || null
      }
    };
  }
  
  /**
   * Get event color based on type and status
   * @param {string} eventType - Event type
   * @param {string} status - Event status
   * @returns {string} Color code
   */
  getEventColor(eventType, status) {
    if (eventType === 'work_order') {
      switch (status) {
        case WORK_ORDER_STATUS.SCHEDULED: return '#3b82f6'; // Blue
        case 'confirmed': return '#10b981'; // Green
        case WORK_ORDER_STATUS.IN_PROGRESS: return '#f59e0b'; // Amber
        case WORK_ORDER_STATUS.COMPLETED: return '#6b7280'; // Gray
        case WORK_ORDER_STATUS.CANCELLED: return '#ef4444'; // Red
        default: return '#6366f1'; // Indigo
      }
    } else {
      switch (status) {
        case WORK_ORDER_STATUS.SCHEDULED: return '#8b5cf6'; // Purple
        case 'confirmed': return '#06b6d4'; // Cyan
        case WORK_ORDER_STATUS.CANCELLED: return '#ef4444'; // Red
        default: return '#6366f1'; // Indigo
      }
    }
  }
  
  /**
   * Get event border color based on type and status
   * @param {string} eventType - Event type
   * @param {string} status - Event status
   * @returns {string} Border color code
   */
  getEventBorderColor(eventType, status) {
    const baseColor = this.getEventColor(eventType, status);
    // Return a slightly darker version for border
    return baseColor;
  }
}

// Export singleton instance
const calendarService = new CalendarService();
export default calendarService;
